import { useState, useEffect, useCallback } from 'react';
import { CloseIcon } from './icons';
import styles from './Toast.module.css';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'info' | 'success' | 'error';
  action?: { label: string; onClick: () => void };
}

interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  return (
    <div className={styles.container} role="status" aria-live="polite">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: string) => void }) {
  // Auto-dismiss pauses while the pointer or keyboard focus is on the toast;
  // leaving restarts the full delay (no remaining-time bookkeeping).
  const [paused, setPaused] = useState(false);
  const [exiting, setExiting] = useState(false);

  // Play the exit animation, then remove — the 150ms defer is far shorter than
  // the 4s/10s dismiss timers, so the drop-oldest cap stays safe.
  const close = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 150);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    if (paused) return;
    // Action toasts carry the undo safety net — give them 10s.
    const delay = toast.action ? 10000 : 4000;
    const timer = setTimeout(close, delay);
    return () => clearTimeout(timer);
  }, [toast.id, toast.action, close, paused]);

  return (
    <div
      className={`${styles.toast} ${styles[toast.type]} ${exiting ? styles.exiting : ''}`}
      role={toast.type === 'error' ? 'alert' : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className={styles.message}>{toast.message}</span>
      {toast.action && (
        <button
          className={styles.action}
          // Fire once: the button stays mounted through the 150ms exit
          // animation, so an unguarded handler could double-fire the undo.
          onClick={() => { if (!exiting) { toast.action!.onClick(); close(); } }}
        >
          {toast.action.label}
        </button>
      )}
      <button className={styles.close} aria-label="Close" onClick={close}><CloseIcon size={14} /></button>
    </div>
  );
}

/** Maximum toasts visible at once. SSE classification bursts can fire 50+
 * events on initial worker drain after import; without a cap each spawns
 * its own toast and the screen fills up. Drop-oldest keeps the most
 * recent N visible — except action-bearing toasts (undo is the only guard
 * on deletes): they are never evicted and expire only by their own timer,
 * so the stack can briefly exceed the cap when several are live. */
const MAX_VISIBLE_TOASTS = 5;

/** Hook for managing toast state. */
export function useToasts() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, type: ToastMessage['type'] = 'info', action?: ToastMessage['action']) => {
    const id = crypto.randomUUID();
    setToasts((prev) => {
      const next = [...prev, { id, message, type, action }];
      if (next.length <= MAX_VISIBLE_TOASTS) return next;
      const dropIndex = next.findIndex((t) => !t.action);
      return dropIndex === -1 ? next : next.filter((_, i) => i !== dropIndex);
    });
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, showToast, dismissToast };
}
